import * as Sentry from '@sentry/react'

/** How Chromium, WebKit and Firefox each word a dynamic import whose chunk never downloaded. */
const MODULE_LOAD_ERROR_MESSAGES = [
	'Failed to fetch dynamically imported module', // Chromium
	'Importing a module script failed', // WebKit
	'error loading dynamically imported module', // Firefox
]

/** Vite's wording for a stylesheet it could not preload. Dormant: every preload site has empty deps. */
const CSS_PRELOAD_ERROR_MESSAGE = 'Unable to preload CSS for'

const PRELOAD_ERROR_MESSAGES = [...MODULE_LOAD_ERROR_MESSAGES, CSS_PRELOAD_ERROR_MESSAGE]

const worded = (error: unknown, wordings: readonly string[]): error is Error =>
	error instanceof Error && wordings.some(wording => error.message.includes(wording))

/**
 * Offline, a chunk that never downloaded is the network, not a crash; online, it is a deploy that never uploaded one.
 * `!== false` because a webview without the flag must not read as offline. Stylesheets report: vite never retries them.
 */
export function dropChunkFailureWhileOffline(
	event: Sentry.ErrorEvent,
	hint: Sentry.EventHint,
): Sentry.ErrorEvent | null {
	if (navigator.onLine !== false) return event

	return worded(hint.originalException, MODULE_LOAD_ERROR_MESSAGES) ? null : event
}

/**
 * Cancelling this event does not silence a chunk: vite then resolves the failed `import()` with `undefined`, which
 * every call site dereferences. Only the stylesheet is cancelled — vite settles it before `baseModule()` runs.
 */
export function registerPreloadErrorHandler(): () => void {
	const handler = (event: Event) => {
		const error = (event as Event & { payload?: unknown }).payload
		if (!worded(error, PRELOAD_ERROR_MESSAGES)) return

		Sentry.addBreadcrumb({
			category: 'preload',
			level: 'warning',
			message: error.message,
		})

		// Captured because cancelling hides it and vite never retries: the view stays unstyled for the session.
		if (error.message.includes(CSS_PRELOAD_ERROR_MESSAGE)) {
			event.preventDefault()
			Sentry.captureException(error)
		}
	}

	window.addEventListener('vite:preloadError', handler)

	return () => window.removeEventListener('vite:preloadError', handler)
}
